import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs, buildArticle } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/warehouse-alexandria.jpg";

export const Route = createFileRoute("/blog/commercial-floor-coating-solutions")({
  head: () => ({
    meta: [
      { title: "Commercial Floor Coating Solutions | MLC Epoxy Floors" },
      { name: "description", content: "Choosing the right floor coating system for warehouses, breweries, restaurants, and retail spaces in the Calgary region. Chemical, traffic, and downtime considerations." },
      { property: "og:title", content: "Commercial Floor Coating Solutions" },
      { property: "og:description", content: "Choosing the right system for warehouses, breweries, and retail." },
      { property: "og:url", content: "/blog/commercial-floor-coating-solutions" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/blog/commercial-floor-coating-solutions" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildArticle({
          title: "Commercial Floor Coating Solutions",
          description: "Choosing the right floor coating system for warehouses, breweries, restaurants, and retail spaces in the Calgary region. Chemical, traffic, and downtime considerations.",
          slug: "commercial-floor-coating-solutions",
          datePublished: "2026-01-30",
        })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbs([
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: "Commercial Floor Coating Solutions", href: "/blog/commercial-floor-coating-solutions" },
        ])),
      },
    ],
  }),
  component: () => (
    <ResourceArticle
      eyebrow="Jan 30, 2026"
      title="Commercial Floor Coating Solutions"
      intro="Commercial floors face challenges residential never sees: forklift traffic, chemical spills, FDA inspections, and zero tolerance for downtime. Here's how we match systems to environments."
      image={heroImg}
      backHref="/blog"
      backLabel="All Articles"
      sections={[
        {
          type: "table",
          heading: "System Recommendations by Environment",
          columns: ["Environment", "Recommended System", "Why"],
          rows: [
            ["Warehouse / distribution", "Heavy-duty epoxy + polyaspartic", "Withstands forklift traffic and pallet drag"],
            ["Brewery / winery", "Urethane mortar with chemical-resistant top", "Handles thermal shock from hot wash-downs"],
            ["Auto repair / dealership", "Hybrid epoxy + polyaspartic with flake", "Hides drips, resists fluids, high gloss for showrooms"],
            ["Restaurant kitchen", "Urethane mortar with integral coving", "Slip-resistant, USDA/FDA compliant, seamless"],
            ["Retail / showroom", "Polished concrete or metallic epoxy", "Aesthetic priority, low-impact traffic"],
            ["Pharmaceutical / lab", "ESD-conductive epoxy", "Controls static, meets cleanroom standards"],
          ],
        },
        {
          type: "list",
          heading: "What Commercial Clients Get Wrong",
          items: [
            { label: "Choosing on price, not lifecycle cost", desc: "A $4/sq ft coating that fails in 3 years costs more than a $9/sq ft system that lasts 15." },
            { label: "Underestimating downtime", desc: "Polyaspartic systems can be installed overnight; epoxy needs 3–5 days. For a busy facility that gap is huge." },
            { label: "Ignoring slope-to-drain", desc: "Coating can't fix bad slope. Fix the substrate first or accept the puddles." },
            { label: "Skipping moisture testing", desc: "Below-grade slabs in commercial buildings almost always need moisture mitigation. Skipping it guarantees failure." },
          ],
        },
        {
          type: "prose",
          heading: "Scheduling Commercial Work",
          body: "We schedule most commercial installs over weekends or overnight to minimize business disruption. With polyaspartic systems, you can close at 6 PM Friday and be operational again by Monday morning — including forklift traffic. For larger facilities we phase the work zone by zone so the business never fully closes.",
        },
        {
          type: "links",
          heading: "Related On This Site",
          intro: "Match a system to your facility — or see real Calgary Region commercial installs.",
          links: [
            { href: "/services/polyaspartic-floor-coatings", label: "Polyaspartic Floor Coatings", desc: "The overnight-install system for facilities that can't go down." },
            { href: "/services/solid-color-epoxy-flooring", label: "Solid Color Epoxy", desc: "Clean industrial look for showrooms and retail." },
            { href: "/gallery?category=Commercial", label: "Commercial Project Gallery", desc: "Warehouses, breweries, and showrooms across the Calgary region." },
          ],
        },
      ]}
    />
  ),
});
